// PhotoImage — displays a Storage Inventory photo.
//
// An asset's `images` entries are storage keys (e.g. "photos/servers/<uuid>.jpg").
// This component exchanges a key for a displayable URL and renders it. Values
// that are already displayable URIs (data:, http(s):, blob:) are used as-is, so
// older/base64 data is still rendered without a round-trip.

import { useEffect, useState } from 'react';
import type { CSSProperties, MouseEvent } from 'react';
import { storageInventoryApi } from '@/lib/storageInventoryClient';

// Module-level caches shared across all instances. Resolved presigned URLs are
// short-lived, so cache them with an expiry and re-resolve once stale (otherwise
// an expired URL would 403 for the rest of the session across all <img>s).
interface CachedUrl {
  url: string;
  expiresAt: number;
}
const urlCache = new Map<string, CachedUrl>();
const inflight = new Map<string, Promise<string>>();

// Presigned GET URLs from the backend live ~1h; treat them as stale a few
// minutes early to avoid edge failures right at expiry.
const URL_CACHE_TTL_MS = 55 * 60 * 1000;

const DIRECT_URI_RE = /^(data:|https?:|blob:)/;

/**
 * Resolve a stored photo value to a displayable URL. Direct URIs pass through;
 * storage keys are exchanged for a URL from the API (cached with expiry, with
 * in-flight dedup).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function resolvePhotoSrc(value: string): Promise<string> {
  if (!value) return Promise.resolve('');
  if (DIRECT_URI_RE.test(value)) return Promise.resolve(value);

  const cached = urlCache.get(value);
  if (cached && cached.expiresAt > Date.now()) return Promise.resolve(cached.url);

  const existing = inflight.get(value);
  if (existing) return existing;

  const request = storageInventoryApi
    .getPhotoUrl(value)
    .then((r) => {
      urlCache.set(value, { url: r.url, expiresAt: Date.now() + URL_CACHE_TTL_MS });
      inflight.delete(value);
      return r.url;
    })
    .catch((err) => {
      inflight.delete(value);
      throw err;
    });

  inflight.set(value, request);
  return request;
}

interface PhotoImageProps {
  photo: string;
  alt: string;
  style?: CSSProperties;
  className?: string;
  onClick?: (e: MouseEvent<HTMLImageElement>) => void;
}

export function PhotoImage({ photo, alt, style, className, onClick }: PhotoImageProps) {
  const [src, setSrc] = useState<string>(() =>
    DIRECT_URI_RE.test(photo) ? photo : ''
  );

  useEffect(() => {
    let active = true;
    resolvePhotoSrc(photo)
      .then((url) => {
        if (active) setSrc(url);
      })
      .catch(() => {
        if (active) setSrc('');
      });
    return () => {
      active = false;
    };
  }, [photo]);

  if (!src) {
    return (
      <div
        className={className}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#eef0f2',
          color: '#888',
          fontSize: 12,
        }}
        aria-label={alt}
      >
        loading…
      </div>
    );
  }

  return <img src={src} alt={alt} style={style} className={className} onClick={onClick} />;
}
